import {Injectable} from '@angular/core';
import {JsonApiResponse} from '@tisa-group/cdk';

import {Action, createSelector, State, StateContext} from '@ngxs/store';
import {GlobalPageElementsService} from '@tisa-group/core';
import {Observable, tap} from 'rxjs';
import {
  UmpireGlobalPageElement,
  UmpireGlobalPageElementSlugEnum,
  UmpireProductTypeEnum,
} from '@tisa-group/models';
import {
  LoadGlobalPageElementBySlug,
  SetGlobalPageElementBySlug,
} from '@store/global-page-element/global-page-element.actions';
import {patch} from '@ngxs/store/operators';
import {OttComponentsHelper} from '@core/utils/ott-components.helper';

export type GlobalPageElementStateModel = Record<
  UmpireGlobalPageElementSlugEnum | string,
  UmpireGlobalPageElement
>;

@State<GlobalPageElementStateModel>({
  name: 'globalPageElement',
  defaults: {},
})
@Injectable()
export class GlobalPageElementState {
  @Action(LoadGlobalPageElementBySlug)
  loadGlobalPageElements(
    ctx: StateContext<GlobalPageElementStateModel>,
    action: LoadGlobalPageElementBySlug,
  ): Observable<JsonApiResponse<UmpireGlobalPageElement>> | undefined {
    const globalPageElement = ctx.getState()[action.slug];

    if (globalPageElement) {
      return;
    }

    return this.globalPageElementsService
      .fetch(action.slug, {...action.params, product: UmpireProductTypeEnum.OTT})
      .pipe(
        tap(res => {
          const croppedComponents =
            action.slug === UmpireGlobalPageElementSlugEnum.ELEMENTS_BELOW_CONTENT
              ? OttComponentsHelper.mapToCroppedComponents(res.data.components)
              : res.data.components;

          ctx.setState(
            patch({
              [action.slug as string]: {
                ...res.data,
                components: croppedComponents,
              },
            }),
          );
        }),
      );
  }

  @Action(SetGlobalPageElementBySlug)
  setGlobalPageElement(
    ctx: StateContext<GlobalPageElementStateModel>,
    action: SetGlobalPageElementBySlug,
  ): void {
    ctx.setState(
      patch({
        [action.globalPageElement.slug as string]: action.globalPageElement,
      }),
    );
  }

  static getGlobalPageElementBySlug(
    slug: UmpireGlobalPageElementSlugEnum,
  ): (state: GlobalPageElementStateModel) => UmpireGlobalPageElement | undefined {
    return createSelector<
      (state: GlobalPageElementStateModel) => UmpireGlobalPageElement | undefined
    >([GlobalPageElementState], state => {
      return state[slug];
    });
  }

  constructor(readonly globalPageElementsService: GlobalPageElementsService) {}
}
